import { useState } from 'react';
import {
  ClockIcon,
  ArrowUturnLeftIcon,
  EyeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDateTime } from '@/utils/formatters';
import type { EmailTemplateVersion } from '@/types/emailTemplate';

interface VersionTimelineProps {
  versions: EmailTemplateVersion[];
  currentVersionId?: string;
  onRevert: (versionId: string) => Promise<void>;
  isReverting?: boolean;
  className?: string;
}

export default function VersionTimeline({
  versions,
  currentVersionId,
  onRevert,
  isReverting = false,
  className,
}: VersionTimelineProps) {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<EmailTemplateVersion | null>(null);

  const handleViewVersion = (version: EmailTemplateVersion) => {
    setSelectedVersion(version);
    setShowPreviewModal(true);
  };

  const handleRevertClick = (version: EmailTemplateVersion) => {
    setSelectedVersion(version);
    setShowRevertModal(true);
  };

  const handleConfirmRevert = async () => {
    if (selectedVersion) {
      await onRevert(selectedVersion.id);
      setShowRevertModal(false);
      setSelectedVersion(null);
    }
  };

  if (versions.length === 0) {
    return (
      <div className={`bg-gray-50 rounded-lg p-6 text-center ${className}`}>
        <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500">No version history available</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

        {/* Version items */}
        <div className="space-y-4">
          {versions.map((version, index) => {
            const isCurrent = version.is_current || version.id === currentVersionId;

            return (
              <div key={version.id} className="relative pl-10">
                {/* Timeline dot */}
                <div
                  className={`absolute left-2.5 w-3 h-3 rounded-full border-2 ${
                    isCurrent
                      ? 'bg-green-500 border-green-500'
                      : 'bg-white border-gray-300'
                  }`}
                />

                {/* Version card */}
                <div
                  className={`bg-white rounded-lg border p-4 ${
                    isCurrent ? 'border-green-300 ring-1 ring-green-100' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-gray-900">
                          {version.version_label}
                        </span>
                        {isCurrent && (
                          <Badge variant="success" dot>
                            Current
                          </Badge>
                        )}
                        {index === 0 && !isCurrent && (
                          <Badge variant="info">Latest</Badge>
                        )}
                      </div>

                      <p className="mt-1 text-sm text-gray-500">
                        {formatDateTime(version.created_at)}
                      </p>

                      {version.change_summary && (
                        <p className="mt-2 text-sm text-gray-600">
                          {version.change_summary}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleViewVersion(version)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-100 rounded transition-colors"
                        title="View this version"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      {!isCurrent && (
                        <button
                          type="button"
                          onClick={() => handleRevertClick(version)}
                          className="p-2 text-gray-400 hover:text-orange-600 hover:bg-gray-100 rounded transition-colors"
                          title="Revert to this version"
                        >
                          <ArrowUturnLeftIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedVersion(null);
        }}
        title={`Version ${selectedVersion?.version_label || ''}`}
        size="xl"
      >
        {selectedVersion && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <ClockIcon className="w-4 h-4" />
              <span>{formatDateTime(selectedVersion.created_at)}</span>
              {selectedVersion.is_current && (
                <Badge variant="success" dot>
                  Current
                </Badge>
              )}
            </div>

            {selectedVersion.change_summary && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">{selectedVersion.change_summary}</p>
              </div>
            )}

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Subject</h4>
              <p className="bg-gray-50 rounded-lg p-3 text-sm">
                {selectedVersion.subject_template}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Content</h4>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <iframe
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                      <head>
                        <style>
                          body { margin: 0; padding: 16px; font-family: Arial, sans-serif; }
                        </style>
                      </head>
                      <body>${selectedVersion.html_content}</body>
                    </html>
                  `}
                  title="Version Preview"
                  className="w-full border-0"
                  style={{ height: '300px' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          </div>
        )}
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
          {selectedVersion && !selectedVersion.is_current && (
            <Button
              leftIcon={<ArrowUturnLeftIcon className="w-4 h-4" />}
              onClick={() => {
                setShowPreviewModal(false);
                handleRevertClick(selectedVersion);
              }}
            >
              Revert to This Version
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Revert Confirmation Modal */}
      <Modal
        isOpen={showRevertModal}
        onClose={() => {
          setShowRevertModal(false);
          setSelectedVersion(null);
        }}
        title="Revert Template"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to revert to version{' '}
            <span className="font-mono font-medium">{selectedVersion?.version_label}</span>?
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This will create a new version with the content from{' '}
              {selectedVersion?.version_label}. The current version will be preserved in the
              history.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowRevertModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRevert}
            loading={isReverting}
            leftIcon={<ArrowUturnLeftIcon className="w-4 h-4" />}
          >
            Revert
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
